import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, Observable, share, Subject } from 'rxjs';

export interface RequestActivityEvent {
  id: string;
  phase: 'start' | 'end';
}

@Injectable({ providedIn: 'root' })
export class DedupeInterceptor implements HttpInterceptor {
  private pendingRequests = new Map<string, Observable<HttpEvent<any>>>();
  private activeIds = new Set<string>();
  private activity$ = new Subject<RequestActivityEvent>();

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const key = this.getRequestKey(req);

    // If request is already pending, return the existing observable
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    // key doubles as the activity id: two callers of an identical in-flight
    // request correctly share one "it's still loading" signal
    this.activeIds.add(key);
    this.activity$.next({ id: key, phase: 'start' });

    // Create new request and cache it. finalize() must come BEFORE share(): placed after, it
    // would run once per subscriber instead of once for the shared execution, double-firing
    // the 'end' activity event whenever a deduped request has more than one subscriber.
    const request$ = next.handle(req).pipe(
      finalize(() => {
        this.pendingRequests.delete(key);
        this.activeIds.delete(key);
        this.activity$.next({ id: key, phase: 'end' });
      }),
      share(),
    );

    this.pendingRequests.set(key, request$);
    return request$;
  }

  /** Snapshot of request ids currently in flight, for correlating a click against activityEvents$. */
  snapshotActiveIds(): ReadonlySet<string> {
    return new Set(this.activeIds);
  }

  get activityEvents$(): Observable<RequestActivityEvent> {
    return this.activity$.asObservable();
  }

  private getRequestKey(req: HttpRequest<any>): string {
    // Create unique key based on method + URL + params
    return `${req.method}:${req.urlWithParams}`;
  }
}
